/* eslint-disable i18next/no-literal-string */
import React, { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import getEspaciosCasaCategories, {
    mapEspaciosCasaItems,
    type EspacioCasaItem
} from '@pylot-data/api/operations/get-espacios-casa-categories'
import type { CategoryTree, Maybe } from '@pylot-data/pylotschema'
import { useFixedPanelBelowNavbar } from './useFixedPanelBelowNavbar'
import s from './EspaciosCasaMegaPanel.module.scss'

type EspaciosCasaMegaPanelProps = {
    navChildren?: Maybe<Array<Maybe<CategoryTree>>> | null
    onNavigate?: () => void
}

const EspaciosCasaMegaPanel = ({
    navChildren,
    onNavigate
}: EspaciosCasaMegaPanelProps): React.ReactElement => {
    const fallback = mapEspaciosCasaItems(navChildren, 'menu')
    const [items, setItems] = useState<EspacioCasaItem[]>(fallback)
    const panelRef = useFixedPanelBelowNavbar()
    const scrollerRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const [progress, setProgress] = useState(0)
    const [thumbWidthPx, setThumbWidthPx] = useState(742)
    const [atStart, setAtStart] = useState(true)
    const [atEnd, setAtEnd] = useState(true)

    const updateScrollState = () => {
        const el = scrollerRef.current
        if (!el) return
        const max = el.scrollWidth - el.clientWidth
        setAtStart(el.scrollLeft <= 1)
        setAtEnd(max <= 1 || el.scrollLeft >= max - 1)
        setProgress(max <= 0 ? 0 : el.scrollLeft / max)
        const trackW = trackRef.current?.clientWidth ?? el.clientWidth
        setThumbWidthPx(Math.min(742, trackW * 0.5))
    }

    const scrollByItem = (direction: 1 | -1) => {
        const el = scrollerRef.current
        if (!el) return
        const item = el.querySelector<HTMLElement>('[data-espacio-menu-item]')
        if (!item) return
        const gap =
            Number.parseFloat(getComputedStyle(el).columnGap || '0') || 0
        el.scrollBy({ left: direction * (item.offsetWidth + gap), behavior: 'smooth' })
    }

    useEffect(() => {
        let cancelled = false
        getEspaciosCasaCategories({ imageKind: 'menu' })
            .then((data) => {
                if (!cancelled && data.length) setItems(data)
            })
            .catch(() => {
                /* fallback local */
            })
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        updateScrollState()
        window.addEventListener('resize', updateScrollState)
        return () => window.removeEventListener('resize', updateScrollState)
    }, [items])

    const trackW = trackRef.current?.clientWidth ?? 0
    const thumbLeft =
        trackW > 0 ? progress * Math.max(trackW - thumbWidthPx, 0) : 0

    return (
        <div
            ref={panelRef}
            className={s.panel}
            role="dialog"
            aria-label="Espacios de la casa"
        >
            <div className={s.inner}>
                <p className={s.eyebrow}>Inspírate</p>
                <p className={s.subtitle}>
                    Descubre ideas para cada espacio de tu hogar
                </p>
                <div className={s.rail}>
                <div
                    ref={scrollerRef}
                    className={s.row}
                    onScroll={updateScrollState}
                >
                    {items.map((item) => (
                        <Link
                            key={item.href + item.name}
                            href={item.href}
                            className={s.card}
                            data-espacio-menu-item
                            onClick={onNavigate}
                        >
                            <span className={s.imageWrap}>
                                {item.image ? (
                                    <img
                                        src={item.image}
                                        alt=""
                                        className={s.image}
                                    />
                                ) : (
                                    <span className={s.placeholder} />
                                )}
                                <span className={s.label}>{item.name}</span>
                            </span>
                        </Link>
                    ))}
                </div>
                {!atStart ? (
                    <button
                        type="button"
                        className={`${s.arrow} ${s.prev}`}
                        aria-label="Anterior"
                        onClick={() => scrollByItem(-1)}
                    />
                ) : null}
                {!atEnd ? (
                    <button
                        type="button"
                        className={`${s.arrow} ${s.next}`}
                        aria-label="Siguiente"
                        onClick={() => scrollByItem(1)}
                    />
                ) : null}
                </div>
                <div ref={trackRef} className={s.track} aria-hidden>
                    <span
                        className={s.thumb}
                        style={{
                            width: `${thumbWidthPx}px`,
                            left: `${thumbLeft}px`
                        }}
                    />
                </div>
            </div>
        </div>
    )
}

export default EspaciosCasaMegaPanel
